/**
 * types.ts - the Workflows tab's wire types.
 *
 * Mirrors `backend/workflows/protocol.py` (pydantic, extra="forbid" on the
 * server side - unknown fields in a PUT body are rejected there). Node ids
 * deliberately match the durable engine's runtime correlation ids where one
 * exists (step ids, `sleep-N`, `signal-<name>-N`); visualisation-only nodes
 * (`__branch-0`, `__fork-0`, …) start with `__`.
 */

export type StepKind =
  | 'step'
  | 'sleep'
  | 'wait'
  | 'branch'
  | 'join'
  | 'fork'
  | 'loop'
  | 'subworkflow'
  | 'opaque';

export type EdgeKind = 'flow' | 'rollback';

export interface WorkflowStep {
  id: string;
  kind: StepKind;
  label: string;
  /** Kind-specific detail: signal name, duration, condition text, … */
  detail?: string | null;
  /** Source line in the workflow module. */
  line?: number | null;
  /** Declared retry count from the step options, when literal. */
  retries?: number | null;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  kind: EdgeKind;
  /** Branch-arm identity ("then"/"else") on a branch node's outgoing edges;
   * unset elsewhere (an unported branch edge is the fallthrough). */
  port?: string | null;
  /** Snap-point anchors from the editor drag (side handle ids like
   * 's-left'/'t-right') — presentation only; the extractor never sets them
   * and the generator ignores them. */
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

/** One workflow as derived from `workflows/<stem>.py` (AST, never imported). */
export interface WorkflowStructure {
  /** Module stem - the key graph/run routes address it by. */
  id?: string | null;
  /** Declared `@workflow("name")` name. */
  name: string;
  file: string;
  description?: string | null;
  steps: WorkflowStep[];
  edges: WorkflowEdge[];
  warnings: string[];
  error?: string | null;
  /** The cosmetics doc merged in by the index route (absent on detail reads). */
  graph?: WorkflowGraphDoc;
}

// ─── Cosmetics document (workflows/<name>.graph.json) ───────────────────────

export interface Position {
  x: number;
  y: number;
}

export interface Bend {
  x: number;
  y: number;
  mode?: 'sharp' | 'smooth';
}

export interface NodeCosmetics {
  position?: Position;
  /** Display-label override (the step id stays the identity). */
  label?: string;
  /** Free-form annotation, rendered as a chip on the node. */
  note?: string;
  /** Accent colour override (border + icon + label chip). */
  color?: string;
}

export interface EdgeCosmetics {
  /** Breakpoint on this edge: the worker suspends before the target step. */
  breakpoint?: boolean;
  bends?: Bend[];
  /** Label position along the path, t ∈ [0, 1]. */
  labelT?: number;
  /** Stroke colour override. */
  color?: string;
  /** Drawn snap-point anchors ('s-right'/'t-left', a branch's 'then'/'else')
   * — persisted through Apply so a saved edge stays pinned where the user
   * dropped it (the re-derived structure never carries handles). */
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

export interface WorkflowGraphDoc {
  version: number;
  nodes: Record<string, NodeCosmetics>;
  edges: Record<string, EdgeCosmetics>;
  /** Run-level pause (the gate suspends before every subsequent step). */
  paused: boolean;
  /** Step ids armed for a one-shot stop (step-through). */
  oneShots: string[];
}

export function emptyGraphDoc(): WorkflowGraphDoc {
  return { version: 1, nodes: {}, edges: {}, paused: false, oneShots: [] };
}

// ─── Graph ⇄ code editor (detail + source apply) ─────────────────────────────

/**
 * Verbatim statement slices keyed by node id - what makes graph → code
 * generation lossless: the editor carries them through topology edits and
 * the backend re-emits them verbatim, so argument wiring and returns
 * survive. Only main()'s orchestration is ever regenerated.
 */
export interface WorkflowAuthoring {
  /** node id → its statement (`v = await fn(args)`, a branch's `<test>`,
   * a loop's full header, a fork's whole `ctx.parallel` statement). */
  statements: Record<string, string>;
  /** node id → plain statements emitted before it. */
  pre: Record<string, string[]>;
  /** node id → plain statements emitted after it at a path/arm end. */
  post: Record<string, string[]>;
  /** node id → a `return <expr>` that ended the path after it. */
  returns: Record<string, string>;
}

/** GET /workflows/{wf} - edit-mode init: structure + slices + cosmetics +
 * whether the workflow round-trips (with human-readable blockers when it
 * doesn't). */
export interface WorkflowDetail {
  structure: WorkflowStructure;
  authoring: WorkflowAuthoring;
  graph: WorkflowGraphDoc;
  editable: boolean;
  blockers: string[];
  /** Node ids derived from preserved defs (rollback relations, subworkflow
   * side-links) - rendered without connection handles, never walked. */
  decorations: string[];
}

/** PUT /workflows/{wf}/source body - the edited draft plus the slices it
 * started from. */
export interface WorkflowSourceRequest {
  steps: WorkflowStep[];
  edges: WorkflowEdge[];
  statements: Record<string, string>;
  pre: Record<string, string[]>;
  post: Record<string, string[]>;
  returns: Record<string, string>;
  decorations: string[];
  /** node id → edited primary param (branch condition, loop header, sleep
   * duration, signal name, subworkflow target) - only when actually edited,
   * so untouched statements re-emit verbatim. */
  params: Record<string, string>;
  /** old step id → new id (the def's declared id is rewritten in place). */
  renames: Record<string, string>;
  dryRun: boolean;
  /** Draft node positions - upserted into the cosmetics doc on Apply. */
  positions: Record<string, Position>;
}

/** PUT /workflows/{wf}/source response - the generated source (also on
 * validation failure, so the preview can show it) + the draft-id →
 * re-extracted-id remap (counter-derived ids renumber with topology). */
export interface WorkflowSourceResult {
  source: string;
  valid: boolean;
  errors: string[];
  warnings: string[];
  structure?: WorkflowStructure | null;
  remap: Record<string, string>;
  wrote: boolean;
}

export function emptyAuthoring(): WorkflowAuthoring {
  return { statements: {}, pre: {}, post: {}, returns: {} };
}

// ─── Execution status (populated from run events; W3 wires the source) ─────

export type StepStatus =
  | 'idle'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'retrying'
  | 'waiting'
  | 'pausedBp';

export interface StepRuntime {
  status: StepStatus;
  attempts: number;
  /** ms between first start and the latest event, when known. */
  durationMs?: number;
  /** Last error message (failed/retrying). */
  error?: string;
  /** Captured step input (the gate's webhook, truncated server-side). */
  input?: unknown;
  /** Captured step output. */
  output?: unknown;
}

export type StepRuntimeMap = Record<string, StepRuntime>;
